import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import BACKEND_URL from "../../config/config";
import { useLanguage } from "../../context/LanguageContext";

const formatLimit = (value, t) =>
  value === null || value === undefined ? t.unlimited : value;

const getStatus = (code, t) => {
  if (code.limit_reached) {
    return { label: t.exhausted, className: "quotaBadge quotaBadgeDanger" };
  }

  if (!code.is_active) {
    return { label: t.inactive, className: "quotaBadge quotaBadgeMuted" };
  }

  if (code.is_available) {
    return { label: t.available, className: "quotaBadge quotaBadgeSuccess" };
  }

  return { label: t.notAvailable, className: "quotaBadge quotaBadgeMuted" };
};

const normalizeLimitInput = (value) => {
  const trimmed = String(value).trim();
  if (trimmed === "") return null;

  return Number(trimmed);
};

const InvitationCodes = () => {
  const { language } = useLanguage();
  const [codes, setCodes] = useState([]);
  const [draftLimits, setDraftLimits] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const t = {
    EN: {
      unlimited: "No limit",
      exhausted: "Exhausted",
      inactive: "Inactive",
      available: "Available",
      notAvailable: "Not available",
      loadError: "Invitation codes could not be loaded.",
      loadErrorMessage: "Codes could not be loaded. Refresh the page and try again.",
      invalidLimit: "The limit must be an integer greater than or equal to 0, or be empty for no limit.",
      invalidLimitFromServer: "The entered limit is not valid.",
      codeNotFound: "The code no longer exists.",
      saveError: "The limit could not be saved.",
      missingUpdatedCode: "The server response did not include the updated code.",
      successMessage: (code) => `Quota updated for ${code}.`,
      pageTitle: "Invitation quota management",
      pageSubtitle:
        "Manage usage limits only. The active or inactive state is controlled internally by the backend.",
      backToPanel: "Back to panel",
      total: "Total",
      searchCode: "Search code",
      searchPlaceholder: "Ex: PROSHALE26",
      status: "Status",
      all: "All",
      code: "Code",
      used: "Used",
      limit: "Limit",
      remaining: "Remaining",
      active: "Active",
      newLimit: "New limit",
      action: "Action",
      yes: "Yes",
      no: "No",
      loading: "Loading codes...",
      noResults: "There are no codes for the selected filters.",
      saving: "Saving...",
      save: "Save",
    },
    ES: {
      unlimited: "Sin tope",
      exhausted: "Agotado",
      inactive: "Inactivo",
      available: "Disponible",
      notAvailable: "No disponible",
      loadError: "No se pudieron cargar los códigos de invitación.",
      loadErrorMessage:
        "No se pudieron cargar los códigos. Actualizá la página e intentá nuevamente.",
      invalidLimit:
        "El límite debe ser un número entero mayor o igual a 0, o quedar vacío para sin tope.",
      invalidLimitFromServer: "El límite ingresado no es válido.",
      codeNotFound: "El código ya no existe.",
      saveError: "No se pudo guardar el límite.",
      missingUpdatedCode: "La respuesta del servidor no incluyó el código actualizado.",
      successMessage: (code) => `Cupo actualizado para ${code}.`,
      pageTitle: "Gestión de cupos de invitación",
      pageSubtitle:
        "Administrá solo los límites de uso. El estado activo o inactivo se controla internamente desde backend.",
      backToPanel: "Volver al panel",
      total: "Total",
      searchCode: "Buscar código",
      searchPlaceholder: "Ej: PROSHALE26",
      status: "Estado",
      all: "Todos",
      code: "Código",
      used: "Usados",
      limit: "Límite",
      remaining: "Restantes",
      active: "Activo",
      newLimit: "Nuevo límite",
      action: "Acción",
      yes: "Sí",
      no: "No",
      loading: "Cargando códigos...",
      noResults: "No hay códigos para los filtros seleccionados.",
      saving: "Guardando...",
      save: "Guardar",
    },
  }[language];

  const syncDraftLimits = (items) => {
    const nextDrafts = {};
    items.forEach((code) => {
      nextDrafts[code.id_invitation_code] =
        code.usage_limit === null || code.usage_limit === undefined
          ? ""
          : String(code.usage_limit);
    });
    setDraftLimits(nextDrafts);
  };

  useEffect(() => {
    let cancelled = false;

    const loadCodes = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${BACKEND_URL}/config/admin/invitation-codes`);
        if (!res.ok) {
          throw new Error(t.loadError);
        }

        const data = await res.json();
        const nextCodes = Array.isArray(data?.codes) ? data.codes : [];

        if (cancelled) return;

        setCodes(nextCodes);
        syncDraftLimits(nextCodes);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(
            t.loadErrorMessage
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCodes();

    return () => {
      cancelled = true;
    };
  }, [t.loadError, t.loadErrorMessage]);

  const summary = useMemo(
    () => ({
      total: codes.length,
      available: codes.filter((code) => code.is_available).length,
      exhausted: codes.filter((code) => code.limit_reached).length,
      inactive: codes.filter((code) => !code.is_active).length,
      unlimited: codes.filter((code) => code.usage_limit === null).length,
    }),
    [codes]
  );

  const filteredCodes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return codes.filter((code) => {
      const matchesSearch = normalizedSearch
        ? String(code.code || "").toLowerCase().includes(normalizedSearch)
        : true;

      if (!matchesSearch) return false;

      if (statusFilter === "available") return code.is_available;
      if (statusFilter === "exhausted") return code.limit_reached;
      if (statusFilter === "inactive") return !code.is_active;
      if (statusFilter === "unlimited") return code.usage_limit === null;

      return true;
    });
  }, [codes, search, statusFilter]);

  const updateDraftLimit = (id, value) => {
    setDraftLimits((prev) => ({
      ...prev,
      [id]: value,
    }));
    setSuccessMessage("");
    setError("");
  };

  const saveLimit = async (code) => {
    const rawValue = draftLimits[code.id_invitation_code] ?? "";
    const usageLimit = normalizeLimitInput(rawValue);

    if (
      usageLimit !== null &&
      (!Number.isInteger(usageLimit) || usageLimit < 0)
    ) {
      setError(
        t.invalidLimit
      );
      setSuccessMessage("");
      return;
    }

    try {
      setSavingId(code.id_invitation_code);
      setError("");
      setSuccessMessage("");

      const res = await fetch(
        `${BACKEND_URL}/config/admin/invitation-codes/${code.id_invitation_code}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ usage_limit: usageLimit }),
        }
      );

      if (!res.ok) {
        if (res.status === 400) {
          throw new Error(t.invalidLimitFromServer);
        }
        if (res.status === 404) {
          throw new Error(t.codeNotFound);
        }
        throw new Error(t.saveError);
      }

      const data = await res.json();
      const updatedCode = data?.code;

      if (!updatedCode) {
        throw new Error(t.missingUpdatedCode);
      }

      setCodes((prev) =>
        prev.map((item) =>
          item.id_invitation_code === updatedCode.id_invitation_code
            ? updatedCode
            : item
        )
      );
      setDraftLimits((prev) => ({
        ...prev,
        [updatedCode.id_invitation_code]:
          updatedCode.usage_limit === null || updatedCode.usage_limit === undefined
            ? ""
            : String(updatedCode.usage_limit),
      }));
      setSuccessMessage(t.successMessage(updatedCode.code));
    } catch (err) {
      console.error(err);
      setError(err.message || t.saveError);
    } finally {
      setSavingId(null);
    }
  };

  const hasDraftChanges = (code) => {
    const currentValue =
      code.usage_limit === null || code.usage_limit === undefined
        ? ""
        : String(code.usage_limit);
    return (draftLimits[code.id_invitation_code] ?? "") !== currentValue;
  };

  return (
    <div className="container">
      <div className="configPageHeader configPageHeaderWithAction">
        <div>
          <h1>{t.pageTitle}</h1>
          <p>
            {t.pageSubtitle}
          </p>
        </div>

        <Link to="/panelecj" className="btnPrint configHeaderLink">
          {t.backToPanel}
        </Link>
      </div>

      <section className="quotaSummaryGrid">
        <div className="card quotaSummaryCard">
          <span>{t.total}</span>
          <strong>{summary.total}</strong>
        </div>
        <div className="card quotaSummaryCard">
          <span>{t.available}</span>
          <strong>{summary.available}</strong>
        </div>
        <div className="card quotaSummaryCard">
          <span>{t.exhausted}</span>
          <strong>{summary.exhausted}</strong>
        </div>
        <div className="card quotaSummaryCard">
          <span>{t.inactive}</span>
          <strong>{summary.inactive}</strong>
        </div>
        <div className="card quotaSummaryCard">
          <span>{t.unlimited}</span>
          <strong>{summary.unlimited}</strong>
        </div>
      </section>

      <section className="card">
        <div className="quotaToolbar">
          <div>
            <label>{t.searchCode}</label>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t.searchPlaceholder}
            />
          </div>

          <div>
            <label>{t.status}</label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">{t.all}</option>
              <option value="available">{t.available}</option>
              <option value="exhausted">{t.exhausted}</option>
              <option value="inactive">{t.inactive}</option>
              <option value="unlimited">{t.unlimited}</option>
            </select>
          </div>
        </div>

        {error && <p className="required quotaMessage">{error}</p>}
        {successMessage && <p className="success quotaMessage">{successMessage}</p>}

        {loading ? (
          <p>{t.loading}</p>
        ) : (
          <div className="quotaTableWrapper">
            <table className="quotaTable">
              <thead>
                <tr>
                  <th>{t.code}</th>
                  <th>{t.used}</th>
                  <th>{t.limit}</th>
                  <th>{t.remaining}</th>
                  <th>{t.active}</th>
                  <th>{t.status}</th>
                  <th>{t.newLimit}</th>
                  <th>{t.action}</th>
                </tr>
              </thead>

              <tbody>
                {filteredCodes.length > 0 ? (
                  filteredCodes.map((code) => {
                    const status = getStatus(code, t);
                    const changed = hasDraftChanges(code);
                    const isSaving = savingId === code.id_invitation_code;

                    return (
                      <tr key={code.id_invitation_code}>
                        <td>
                          <strong>{code.code}</strong>
                        </td>
                        <td>{code.usage_count}</td>
                        <td>{formatLimit(code.usage_limit, t)}</td>
                        <td>{formatLimit(code.remaining_quota, t)}</td>
                        <td>{code.is_active ? t.yes : t.no}</td>
                        <td>
                          <span className={status.className}>{status.label}</span>
                        </td>
                        <td>
                          <input
                            className="quotaLimitInput"
                            type="number"
                            min="0"
                            step="1"
                            value={draftLimits[code.id_invitation_code] ?? ""}
                            onChange={(event) =>
                              updateDraftLimit(
                                code.id_invitation_code,
                                event.target.value
                              )
                            }
                            placeholder={t.unlimited}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className={changed ? undefined : "btnPrint"}
                            disabled={!changed || isSaving}
                            onClick={() => saveLimit(code)}
                          >
                            {isSaving ? t.saving : t.save}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8">{t.noResults}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default InvitationCodes;
